import { RUNTIME_CONFIG } from '../config/runtimeConfig';
import { CONTACT_INFO } from '../config/contactConfig';
import { validateLeadPayload } from '../utils/payloadSchemas';

const LEAD_STORAGE_KEY = 'pb_booking_leads';
const LEAD_WEBHOOK_QUEUE_KEY = 'pb_booking_lead_webhook_queue';
const MAX_WEBHOOK_RETRIES = 5;
const RETRY_BASE_DELAY_MS = 30 * 1000;
let deliveryInitialized = false;

const cleanPhone = (value = '') => String(value).replace(/\D/g, '');

const normalizePhoneForWhatsApp = (value = '', defaultCountryCode = '91') => {
  const digits = cleanPhone(value);
  if (!digits) return '';
  if (digits.length === 10) {
    return `${defaultCountryCode}${digits}`;
  }
  if (digits.startsWith('00')) {
    return digits.slice(2);
  }
  return digits;
};

const formatCurrency = (amount) => {
  const numeric = Number(amount || 0);
  return `INR ${numeric.toLocaleString('en-IN')}`;
};

const createLeadId = () => {
  const now = new Date();
  const stamp = now.toISOString().replace(/[-:TZ.]/g, '').slice(0, 14);
  const random = Math.floor(Math.random() * 900 + 100);
  return `LD-${stamp}-${random}`;
};

const readStoredLeads = () => {
  try {
    const raw = localStorage.getItem(LEAD_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const writeStoredLeads = (leads) => {
  try {
    localStorage.setItem(LEAD_STORAGE_KEY, JSON.stringify(leads));
    return true;
  } catch (error) {
    return false;
  }
};

const readWebhookQueue = () => {
  try {
    const raw = localStorage.getItem(LEAD_WEBHOOK_QUEUE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const writeWebhookQueue = (items) => {
  try {
    localStorage.setItem(LEAD_WEBHOOK_QUEUE_KEY, JSON.stringify(items));
    return true;
  } catch (error) {
    return false;
  }
};

const getRetryDelayMs = (attempts) => {
  const exponent = Math.max(0, Number(attempts || 0));
  return Math.min(RETRY_BASE_DELAY_MS * Math.pow(2, exponent), 30 * 60 * 1000);
};

const enqueueWebhookRetry = (lead, reason = 'webhook-failed') => {
  const queue = readWebhookQueue();
  const existingIndex = queue.findIndex((item) => item?.lead?.id === lead?.id);
  const existing = existingIndex >= 0 ? queue[existingIndex] : null;
  const nextAttempts = Number(existing?.attempts || 0) + 1;

  if (nextAttempts > MAX_WEBHOOK_RETRIES) {
    if (existingIndex >= 0) {
      queue.splice(existingIndex, 1);
      writeWebhookQueue(queue);
    }
    return false;
  }

  const nextItem = {
    lead,
    attempts: nextAttempts,
    reason,
    queuedAt: existing?.queuedAt || new Date().toISOString(),
    nextRetryAt: Date.now() + getRetryDelayMs(nextAttempts - 1)
  };

  if (existingIndex >= 0) {
    queue[existingIndex] = nextItem;
  } else {
    queue.push(nextItem);
  }

  writeWebhookQueue(queue);
  return true;
};

const upsertLeadToLocalStorage = (lead) => {
  const current = readStoredLeads();
  const next = [lead, ...current].slice(0, 300);
  return writeStoredLeads(next);
};

const openWhatsAppDraft = (phone, message) => {
  const normalized = normalizePhoneForWhatsApp(phone);
  if (!normalized || typeof window === 'undefined') {
    return false;
  }

  const url = `https://wa.me/${normalized}?text=${encodeURIComponent(message)}`;
  window.open(url, '_blank', 'noopener,noreferrer');
  return true;
};

const buildAdminMessage = (lead) => {
  return [
    `New booking lead: ${lead.id}`,
    `Service: ${lead.serviceTitle}`,
    `Package: ${lead.packageName}`,
    `Date/Time: ${lead.eventDate || '-'} ${lead.eventTime || ''}`.trim(),
    `Customer: ${lead.customerName}`,
    `Phone: ${lead.customerPhone || '-'}`,
    `Email: ${lead.customerEmail || '-'}`,
    `City: ${lead.city || '-'}`,
    `Amount: ${formatCurrency(lead.totalPrice)}`,
    `Source: ${lead.source}`
  ].join('\n');
};

const buildCustomerMessage = (lead) => {
  return [
    `Booking request received`,
    `Lead ID: ${lead.id}`,
    `Service: ${lead.serviceTitle}`,
    `Package: ${lead.packageName}`,
    `Date/Time: ${lead.eventDate || '-'} ${lead.eventTime || ''}`.trim(),
    `Amount: ${formatCurrency(lead.totalPrice)}`,
    `Our team will confirm shortly.`
  ].join('\n');
};

const buildPanditMessage = (lead) => {
  return [
    `New service lead assigned`,
    `Lead ID: ${lead.id}`,
    `Service: ${lead.serviceTitle}`,
    `Date/Time: ${lead.eventDate || '-'} ${lead.eventTime || ''}`.trim(),
    `Customer: ${lead.customerName}`,
    `City: ${lead.city || '-'}`,
    `Please review availability.`
  ].join('\n');
};

const buildNotificationPlan = (lead, options = {}) => {
  const notifyUser = options.notifyUser !== false;
  const notifyPandit = options.notifyPandit !== false;
  const notifyAdmin = options.notifyAdmin !== false;

  const adminPhone = normalizePhoneForWhatsApp(
    RUNTIME_CONFIG.LEAD_ADMIN_WHATSAPP || CONTACT_INFO.WHATSAPP
  );
  const panditPhone = normalizePhoneForWhatsApp(
    lead.panditPhone || RUNTIME_CONFIG.LEAD_DEFAULT_PANDIT_WHATSAPP
  );
  const customerPhone = normalizePhoneForWhatsApp(lead.customerPhone);

  const customerChannel = lead.customerEmail
    ? 'email'
    : (customerPhone ? 'whatsapp' : 'none');

  return {
    requestedAt: new Date().toISOString(),
    userConfirmation: {
      enabled: notifyUser,
      channel: customerChannel,
      toEmail: lead.customerEmail || '',
      toPhone: customerPhone || '',
      subject: `Booking request received - ${lead.id}`,
      message: buildCustomerMessage(lead)
    },
    panditWhatsApp: {
      enabled: notifyPandit && Boolean(panditPhone),
      toPhone: panditPhone || '',
      message: buildPanditMessage(lead)
    },
    adminWhatsApp: {
      enabled: notifyAdmin && Boolean(adminPhone),
      toPhone: adminPhone || '',
      message: buildAdminMessage(lead)
    }
  };
};

const submitLeadToWebhook = async (lead) => {
  const webhookUrl = RUNTIME_CONFIG.NOTIFICATION_WEBHOOK_URL || RUNTIME_CONFIG.LEAD_WEBHOOK_URL;
  if (!webhookUrl) {
    return { sent: false, reason: 'webhook-not-configured' };
  }

  try {
    const response = await fetch(webhookUrl, {
      method: 'POST',
      headers: {
        // text/plain avoids CORS preflight for browser-to-Apps-Script calls.
        'Content-Type': 'text/plain;charset=utf-8'
      },
      body: JSON.stringify(lead)
    });

    return { sent: response.ok, status: response.status };
  } catch (error) {
    try {
      // Fallback for restrictive CORS responses.
      await fetch(webhookUrl, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'text/plain;charset=utf-8'
        },
        body: JSON.stringify(lead)
      });

      return { sent: true, status: 0, mode: 'no-cors' };
    } catch (fallbackError) {
      return { sent: false, reason: fallbackError?.message || error?.message || 'network-error' };
    }
  }
};

const processWebhookQueue = async () => {
  const queue = readWebhookQueue();
  const webhookUrl = RUNTIME_CONFIG.NOTIFICATION_WEBHOOK_URL || RUNTIME_CONFIG.LEAD_WEBHOOK_URL;
  if (!queue.length || !webhookUrl) {
    return { processed: 0, remaining: queue.length };
  }

  const now = Date.now();
  const remaining = [];
  let processed = 0;

  for (const item of queue) {
    if (!item?.lead) {
      continue;
    }

    if (item.nextRetryAt && Number(item.nextRetryAt) > now) {
      remaining.push(item);
      continue;
    }

    const result = await submitLeadToWebhook(item.lead);
    processed += 1;

    if (!result.sent) {
      const attempts = Number(item.attempts || 0) + 1;
      if (attempts <= MAX_WEBHOOK_RETRIES) {
        remaining.push({
          ...item,
          attempts,
          reason: result.reason || item.reason || 'webhook-failed',
          nextRetryAt: Date.now() + getRetryDelayMs(attempts - 1)
        });
      }
    }
  }

  writeWebhookQueue(remaining);
  return { processed, remaining: remaining.length };
};

const openNotificationDrafts = (lead, notificationPlan, options = {}) => {
  const recipients = Array.isArray(options.openWhatsAppRecipients)
    ? options.openWhatsAppRecipients
    : ['admin'];

  const adminOpened = recipients.includes('admin') && notificationPlan?.adminWhatsApp?.enabled
    ? openWhatsAppDraft(notificationPlan.adminWhatsApp.toPhone, notificationPlan.adminWhatsApp.message)
    : false;

  const panditOpened = recipients.includes('pandit') && notificationPlan?.panditWhatsApp?.enabled
    ? openWhatsAppDraft(notificationPlan.panditWhatsApp.toPhone, notificationPlan.panditWhatsApp.message)
    : false;

  const customerOpened = recipients.includes('customer')
    && notificationPlan?.userConfirmation?.enabled
    && notificationPlan?.userConfirmation?.channel === 'whatsapp'
    ? openWhatsAppDraft(notificationPlan.userConfirmation.toPhone, notificationPlan.userConfirmation.message)
    : false;

  return { adminOpened, panditOpened, customerOpened };
};

export const submitBookingLead = async (leadInput, options = {}) => {
  const validation = validateLeadPayload(leadInput);
  if (!validation.valid) {
    throw new Error(validation.errors.join(' '));
  }

  const lead = {
    id: createLeadId(),
    createdAt: new Date().toISOString(),
    status: 'new',
    source: leadInput.source || 'frontend',
    serviceTitle: leadInput.serviceTitle || 'Service Booking',
    packageName: leadInput.packageName || 'Basic Package',
    totalPrice: Number(leadInput.totalPrice || 0),
    eventDate: leadInput.eventDate || '',
    eventTime: leadInput.eventTime || '',
    customerName: leadInput.customerName || '',
    customerPhone: cleanPhone(leadInput.customerPhone || ''),
    customerEmail: leadInput.customerEmail || '',
    address: leadInput.address || '',
    city: leadInput.city || '',
    state: leadInput.state || '',
    pincode: leadInput.pincode || '',
    notes: leadInput.notes || '',
    panditName: leadInput.panditName || '',
    panditPhone: cleanPhone(leadInput.panditPhone || ''),
    payload: leadInput.payload || null
  };

  const notificationPlan = buildNotificationPlan(lead, options);
  lead.notificationPlan = notificationPlan;

  const stored = upsertLeadToLocalStorage(lead);
  const webhook = await submitLeadToWebhook(lead);

  if (!webhook.sent) {
    enqueueWebhookRetry(lead, webhook.reason || 'webhook-failed');
  }

  // Strict opt-in: WhatsApp drafts open only when caller explicitly passes openWhatsApp: true.
  const shouldOpenWhatsApp = options.openWhatsApp === true;
  let draftOpenResult = null;
  if (shouldOpenWhatsApp) {
    draftOpenResult = openNotificationDrafts(lead, notificationPlan, options);
  }

  return {
    lead,
    stored,
    webhook,
    notificationPlan,
    draftOpenResult
  };
};

export const getStoredBookingLeads = () => readStoredLeads();
export const getQueuedLeadDeliveries = () => readWebhookQueue();

export const flushLeadWebhookQueue = async () => processWebhookQueue();

export const initializeLeadDelivery = () => {
  if (deliveryInitialized || typeof window === 'undefined') {
    return;
  }

  deliveryInitialized = true;

  processWebhookQueue();
  window.addEventListener('online', () => {
    processWebhookQueue();
  });
};
